import {createSlice, PayloadAction} from "@reduxjs/toolkit";
import {
    DialogsReduxSliceState,
    DialogsReduxSliceStateBaseEntry,
    DialogsReduxSliceStateConfirmationEntry,
    DialogsReduxSliceStateCreateSettingsDataForGroupEntry,
    DialogsReduxSliceStateDownloadCodeEntry,
    DialogsReduxSliceStateFieldLogsHistoryEntry,
    DialogsReduxSliceStateManageOrganizationGroupsEntry,
    DialogsReduxSliceStateRemoveConfirmationEntry,
    DialogsReduxSliceStateUpsertRegisteredLocationEntry,
    DialogsReduxSliceStateViewInstallationGuideEntry,
} from "./type-declerations";
import {Draft} from "immer";


const baseInitialState: DialogsReduxSliceStateBaseEntry = {
    open: false,
    disableAutoFocus: false,
    disableEnforceFocus: false,
    disableEscapeKeyDown: false,
    disableBackdropClick: false,
    hideCloseButton: false,
    disablePortal: false,
    disableRestoreFocus: false,
    disableScrollLock: false,
    hideBackdrop: false,
    keepMounted: false,
    fullWidth: true,
    maxWidth: "sm",
    onClose: (): void => void 0,
}

const initialState: DialogsReduxSliceState = {
    removeConfirmation: {
        ...baseInitialState,
        onClose: async () => false,
    },
    confirmation: {
        ...baseInitialState,
        maxWidth: 'xs',
        onClose: async () => false,
    },
    viewInstallationGuide: {
        ...baseInitialState,
        maxWidth: 'xs',
    },
    downloadCode: {
        ...baseInitialState,
        onClose: async () => false,
    },
    upsertRegisteredLocation: {
        ...baseInitialState,
    },
    createSettingsDataForGroup: {
        ...baseInitialState,
        maxWidth: 'xs',
        onClose: async () => false,
    },
    fieldLogsHistory: {
        ...baseInitialState,
    },
    manageOrganizationGroups: {
        ...baseInitialState,
    },
}

const sliceName = 'dialogs';

/**
 * Merges the given state object with the provided action.
 *
 * * if the value of [open] property of [action.payload] is set to false, resets the [type] property of the state.
 * @param state
 * @param action
 */
const mergePayloadWithState = (
    state: any,
    action: PayloadAction<Partial<DialogsReduxSliceStateBaseEntry>>,
): DialogsReduxSliceState => {
    const type = action.type.replace(`${sliceName}/`, '') as keyof DialogsReduxSliceState;
    if (action?.payload?.open === false) {
        return {
            ...state,
            [type]: {
                ...state[type],
                ...initialState[type],
            },
        };
    }
    let data;
    switch (typeof action?.payload?.data) {
        case "object":
        case "undefined": {
            if (Array.isArray(action?.payload?.data)) {
                data = [
                    ...((initialState[type]?.data as Array<any>) ?? []),
                    ...(action?.payload?.data ?? []),
                ];
                break;
            }
            data = {
                ...(initialState[type]?.data ?? {}),
                ...(action?.payload?.data ?? {}),
            };
            break;
        }
        default:
            data = action?.payload?.data ?? initialState[type].data;
    }
    return {
        ...state,
        [type]: {
            ...state[type],
            ...action?.payload,
            data: data,
        },
    };
}

/**
 * The Redux state, action creators, types and reducers of the Dialogs slice.
 */
export const DialogsReduxSlice = createSlice({
    name: sliceName,
    initialState,
    reducers: {
        removeConfirmation: (
            state: Draft<DialogsReduxSliceState>,
            action: PayloadAction<Partial<DialogsReduxSliceStateRemoveConfirmationEntry>>
        ) => mergePayloadWithState(state, action),
        confirmation: (
            state,
            action: PayloadAction<Partial<DialogsReduxSliceStateConfirmationEntry>>
        ) => mergePayloadWithState(state, action),
        viewInstallationGuide: (
            state,
            action: PayloadAction<Partial<DialogsReduxSliceStateViewInstallationGuideEntry>>
        ) => mergePayloadWithState(state, action),
        downloadCode: (
            state,
            action: PayloadAction<Partial<DialogsReduxSliceStateDownloadCodeEntry>>
        ) => mergePayloadWithState(state, action),
        upsertRegisteredLocation: (
            state,
            action: PayloadAction<Partial<DialogsReduxSliceStateUpsertRegisteredLocationEntry>>
        ) => mergePayloadWithState(state, action),
        createSettingsDataForGroup: (
            state,
            action: PayloadAction<Partial<DialogsReduxSliceStateCreateSettingsDataForGroupEntry>>
        ) => mergePayloadWithState(state, action),
        fieldLogsHistory: (
            state,
            action: PayloadAction<Partial<DialogsReduxSliceStateFieldLogsHistoryEntry>>
        ) => mergePayloadWithState(state, action),
        manageOrganizationGroups: (
            state,
            action: PayloadAction<Partial<DialogsReduxSliceStateManageOrganizationGroupsEntry>>
        ) => mergePayloadWithState(state, action),
    },
})

export * from './type-declerations';
export default DialogsReduxSlice;
